<template>
  <div v-show="!isClosed" class="Status" :class="`Status--${status}`">
    <Icon
      class="Status-icon"
      :icon="statusIcon"
      size="lg"
    />

    <div class="Status-content">
      <div class="Status-message">
        <slot>
          <span v-html="props.message" />
        </slot>
      </div>

      <div v-if="actionPrimary || actionSecondary" class="Status-actions">
        <ActionButton
          v-if="actionPrimary"
          variant="primary"
          size="sm"
          @click="emit('clickPrimary')"
        >
          {{ actionPrimary }}
        </ActionButton>

        <ActionButton
          v-if="actionSecondary"
          variant="secondary"
          size="sm"
          @click="emit('clickSecondary')"
        >
          {{ actionSecondary }}
        </ActionButton>
      </div>
    </div>

    <IconButton
      v-if="!persistent"
      class="Status-closeButton"
      icon="window-close"
      type="button"
      size="lg"
      ariaLabel="Close notification"
      @click="close"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import type { StatusProps, StatusEmits } from './Status.model';

const props = withDefaults(defineProps<StatusProps>(), {
  status: 'info',
  persistent: false,
});

const emit = defineEmits<StatusEmits>();

const isClosed = ref(false);

watch(() => props.persistent, () => {
  isClosed.value = false;
});

const statusIcon = computed(() => {
  switch (props.status) {
    case 'success':
      return 'check';
    case 'warning':
      return 'alert';
    case 'danger':
      return 'alert-circle';
    case 'info':
    default:
      return 'information';
  }
});

function close() {
  isClosed.value = true;
  emit('close');
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@mixin status-variant(
  $bg-color,
  $border-color,
  $icon-color,
  $text-color,
  $close-icon-color
) {
  background-color: $bg-color;
  border-color: $border-color;

  .Status-icon {
    color: $icon-color;
  }

  .Status-message {
    color: $text-color;
  }

  .Status-closeButton {
    color: $close-icon-color;
  }
}

@layer cvo-ui {
  .Status {
    display: flex;
    align-items: center;
    gap: vars.$dimensions-d-3;
    flex-wrap: nowrap;
    padding: vars.$dimensions-d-3;
    border-style: solid;
    border-width: vars.$molecules-notification-border-stroke;
    border-radius: vars.$molecules-notification-lg-md-border-radius;
    border-inline-start-width: vars.$molecules-notification-lg-md-border-left-stroke;

    &--info {
      @include status-variant(
        $bg-color: vars.$molecules-notification-information-bg-color,
        $border-color: vars.$molecules-notification-information-border-color,
        $icon-color: vars.$molecules-notification-information-icon-color,
        $text-color: vars.$molecules-notification-information-text-color,
        $close-icon-color: vars.$molecules-notification-information-icon-close-color
      );
    }

    &--success {
      @include status-variant(
        $bg-color: vars.$molecules-notification-success-bg-color,
        $border-color: vars.$molecules-notification-success-border-color,
        $icon-color: vars.$molecules-notification-success-icon-color,
        $text-color: vars.$molecules-notification-success-text-color,
        $close-icon-color: vars.$molecules-notification-success-icon-close-color
      );
    }

    &--warning {
      @include status-variant(
        $bg-color: vars.$molecules-notification-warning-bg-color,
        $border-color: vars.$molecules-notification-warning-border-color,
        $icon-color: vars.$molecules-notification-warning-icon-color,
        $text-color: vars.$molecules-notification-warning-text-color,
        $close-icon-color: vars.$molecules-notification-warning-icon-close-color
      );
    }

    &--danger {
      @include status-variant(
        $bg-color: vars.$molecules-notification-danger-bg-color,
        $border-color: vars.$molecules-notification-danger-border-color,
        $icon-color: vars.$molecules-notification-danger-icon-color,
        $text-color: vars.$molecules-notification-danger-text-color,
        $close-icon-color: vars.$molecules-notification-danger-icon-close-color
      );
    }

    // TODO Use container query instead
    // Responsive breakpoint for small screens
    @include responsive.mobile {
      padding: vars.$dimensions-d-2;
      align-items: start;
      border-radius: vars.$molecules-notification-sm-border-radius;
      border-inline-start-width: vars.$molecules-notification-sm-border-left-stroke;
    }

    &-icon {
      align-self: start;
    }

    &-content {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-3;
      flex: 1;

      @include responsive.mobile {
        flex-direction: column;
        align-items: flex-start;
      }
    }

    &-actions {
      display: flex;
      gap: vars.$dimensions-d-4;
      flex-wrap: nowrap;
    }

    &-message {
      flex: 1;
      @include text.text('sm');
      line-height: 100%;

      // TODO Use container query instead
      @include responsive.mobile {
        @include text.text('xs');
      }
    }
  }
}
</style>
